<template>
  <div :styles="{ height: height, maxHeight: height }">
    <h2 v-if="header" class="mb-0">{{ header }}</h2>
    <div v-if="description" class="description">
      {{ description }}
    </div>
    <div v-if="loading">
      <NumeraiLoader />
    </div>
    <canvas
      ref="canvas"
      :style="{
        display: loading ? 'none' : 'block',
        height: height,
        maxHeight: height,
      }"
      :height="height"
      width="fullwidth"
    ></canvas>
  </div>
</template>

<script>
import _ from 'lodash'
import { Line } from 'vue-chartjs'
import NumeraiLoader from '@/components/shared/NumeraiLoader'

const GRID_COLOR = '#eaeaea'
const GRID_COLOR_DARK = '#2e2e30'

export default {
  components: {
    NumeraiLoader,
  },
  extends: Line,
  props: {
    header: { type: String, default: null },
    description: { type: String, default: null },
    loading: { type: Boolean, default: false },
    dataColors: { type: Array, default: () => [] },
    dataLabels: { type: Array, default: () => [] },
    chartData: { type: Array, default: () => [] },
    dashedLines: { type: Array, default: () => [] },
    height: { type: String, default: '300' },
    invertedYAxis: { type: Boolean, default: false },
    yAxisMin: { type: Number, default: null },
    chartLabels: { type: Array, default: null },
    suggestedMin: { type: Number, default: 0 },
    suggestedMax: { type: Number, default: 0 },
    yTicksLimit: { type: Number, default: 9 },
    xTicksLimit: { type: Number, default: null },
    primaryBorderWidth: { type: Number, default: 2 },
    secondaryBorderWidth: { type: Number, default: 2 },
    showTooltip: { type: Boolean, default: true },
    isPercent: { type: Boolean, default: false },
    steppedLine: { type: Boolean, default: false },
    verticalGridLines: { type: Boolean, default: false },
    horizontalGridLines: { type: Boolean, default: true },
    drawYBorder: { type: Boolean, default: true },
    showxTicks: { type: Boolean, default: true },
    showyTicks: { type: Boolean, default: true },
    area: { type: Boolean, default: false },
    areaColorStart: { type: String, default: null },
    areaColorStop: { type: String, default: null },
    maintainAspectRatio: { type: Boolean, default: false },
    chartColors: { type: Array[String], default: () => [] },
    isSingleLine: { type: Boolean, default: false },
    isDarkMode: { type: Boolean, default: false },
    timeUnit: { type: String, default: 'month' },
    legendPosition: { type: String, default: 'top' },
    legend: { type: Boolean, default: false },
  },
  computed: {
    xAxes() {
      if (this.chartLabels) {
        return [
          {
            type: 'category',
            labels: this.chartLabels,
            ticks: {
              autoSkip: true,
              maxTicksLimit: this.xTicksLimit,
              padding: 10,
              fontSize: 10.5,
              fontColor: '#999999',
              display: this.showxTicks,
            },
            gridLines: {
              color: this.isDarkMode ? GRID_COLOR_DARK : GRID_COLOR,
              drawTicks: false,
              drawOnChartArea: this.verticalGridLines,
            },
          },
        ]
      }
      return [
        {
          type: 'time',
          time: {
            unit: this.timeUnit,
            tooltipFormat: 'MMM D, YYYY',
          },
          ticks: {
            labelOffset: !this.showyTicks && !this.drawYBorder ? 20 : null,
            padding: 10,
            fontSize: 10.5,
            fontColor: '#999999',
            display: this.showxTicks,
            maxRotation: this.isSingleLine ? 0 : 50,
          },
          gridLines: {
            color: this.isDarkMode ? GRID_COLOR_DARK : GRID_COLOR,
            drawTicks: false,
            drawOnChartArea: this.verticalGridLines,
            display: this.horizontalGridLines,
            drawBorder: true,
            zeroLineColor: this.isDarkMode ? GRID_COLOR_DARK : GRID_COLOR,
            zeroLineWidth: 1,
          },
        },
      ]
    },
    dataset() {
      return {
        datasets: this.chartData.map((data, index) => ({
          borderColor: this.dataColors[index] || this.getChartColors(index),
          label: this.dataLabels[index],
          data: data,
          fill: this.area ? 'origin' : false,
          backgroundColor: this.area ? this.gradient : undefined,
          pointRadius: 0.5,
          lineTension: 0,
          spanGaps: true,
          borderWidth: this.area ? 0.01 : this.primaryBorderWidth,
          steppedLine: this.steppedLine,
          immediate: false,
          borderDash: this.dashedLines[index] || [],
        })),
      }
    },
    options() {
      const options = {
        responsive: true,
        immediate: false,
        maintainAspectRatio: this.maintainAspectRatio,
        layout: {
          padding: {
            left: 0,
            right: 10,
            top: 10,
            bottom: 10,
          },
        },
        animation: {
          duration: 0,
        },
        title: {
          display: !!this.header,
          text: this.header,
        },
        legend: {
          display: this.legend,
          position: this.legendPosition,
        },
        scales: {
          xAxes: this.xAxes,
          yAxes: [
            {
              ticks: {
                reverse: this.invertedYAxis,
                suggestedMin: this.suggestedMin,
                suggestedMax: this.suggestedMax,
                maxTicksLimit: this.yTicksLimit,
                padding: 10,
                fontSize: 10.5,
                fontColor: '#999999',
                display: this.showyTicks,
                callback: value => {
                  return `${value}${this.isPercent ? '%' : ''}`
                },
              },
              gridLines: {
                color: this.isDarkMode ? GRID_COLOR_DARK : GRID_COLOR,
                zeroLineColor: this.isDarkMode ? GRID_COLOR : GRID_COLOR_DARK,
                zeroLineWidth: this.area ? 0.1 : 0.5,
                drawTicks: false,
                drawOnChartArea: this.horizontalGridLines,
                drawBorder: this.drawYBorder,
              },
            },
          ],
        },
        tooltips: {
          enabled: this.showTooltip,
          intersect: false,
          mode: 'index',
          callbacks: {
            label: function(tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) {
                label += ': '
              }
              label += Math.round(tooltipItem.yLabel * 1000) / 1000
              return label
            },
          },
        },
      }

      if (this.yAxisMin != null) {
        _.set(options, 'scales.yAxes[0].ticks.min', this.yAxisMin)
        _.set(options, 'scales.yAxes[0].gridLines.zeroLineWidth', 0.5)
      }
      return options
    },
  },
  watch: {
    chartData() {
      this.render()
    },
    isDarkMode() {
      this.render()
    },
  },
  mounted() {
    this.render()
  },
  methods: {
    getChartColors(index) {
      const darkModeColors = ['#FFF', '#aaa', '#bbb', '#bbb']
      const lightModeColors = ['#000', '#555', '#666', '#666']
      if (this.chartColors.length && !!this.chartColors[index]) {
        return this.chartColors[index]
      } else if (this.isDarkMode) {
        return darkModeColors[index]
      } else {
        return lightModeColors[index]
      }
    },
    render() {
      if (this.area) {
        this.gradient = this.$refs.canvas
          .getContext('2d')
          .createLinearGradient(0, 0, 0, 400)
        this.gradient.addColorStop(
          0,
          !this.areaColorStart ? 'rgba(33, 150, 243, 1)' : this.areaColorStart,
        )
        this.gradient.addColorStop(
          1,
          !this.areaColorStop ? 'rgba(33, 150, 243, 0.67)' : this.areaColorStop,
        )
      }

      this.renderChart(this.dataset, this.options)
    },
  },
}
</script>

<style scoped>
.description {
  color: var(--color-secondary);
}
</style>
